/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { createRef, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from "react-toastify";
import { api } from "../../../../api/GeneratedApi";
import { CAPTCHA_PUBLIC_KEY, SERVER_URL } from "../../../../config";
import PasscodeInput from "../../../PasscodeInput";
import StyledButton from "../../../StyledButton";
import { SignupFormSection } from "../../Form";
import {
  PasscodeButtonsWrapper,
  PasscodeContainer,
  PasscodeProps,
  PasscodeWrapper,
} from "../ThirdForm";

const ClockTitle = styled.p`
  color: #333333;
  font-size: 16px;
  margin: 30px 0px 5px;
`;

const ResendTitle = styled.div`
  color: #fb972e;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  padding: 5px;

  :hover {
    opacity: 0.7;
  }
`;

export default function TemporaryPasscode({
  setSelectedForm,
  form,
  handleChangeForm,
}: PasscodeProps) {
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState({ min: 2, sec: 59 });
  const [date, setDate] = useState(dayjs().add(180, "seconds"));
  const [enableResend, setEnableResend] = useState(false);

  const [showCapctha, setShowCaptcha] = useState(false);
  const recaptchaRef = createRef();

  function getNewTime() {
    const time = date;
    const now = Date.now();
    const hrs = time.diff(now, "hour");
    const min = time.diff(now, "minutes");
    const sec = time.diff(now, "seconds");

    if (sec - min * 60 >= 0)
      return {
        min: min - hrs * 60,
        sec: sec - min * 60,
      };
    else
      return {
        min: min - hrs * 60,
        sec: 0,
      };
  }

  useEffect(() => {
    const interval = setInterval(() => {
      if (timer.min === 0 && timer.sec === 0) return;
      const time = getNewTime();
      setTimer(time);
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, [date]);

  useEffect(() => {
    if (!timer) return;

    if (timer.min === 0 && timer.sec <= 0) {
      setEnableResend(true);
    }
  }, [timer]);

  async function handleClick() {
    if (form.temporaryPasscode.includes("")) {
      toast("Incorrect passcode", { type: "error" });

      setError("Incorrect passcode");

      setIsLoading(false);
      return;
    }

    const response = await fetch(`${SERVER_URL}/auth/learnistic/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        phone: form.phone,
        password: form.temporaryPasscode.join(""),
      }),
    });
    const body = await response.json();

    if (body?.data?.ok) {
      setSelectedForm(SignupFormSection.ChoosePasscodeLearnistic);
    } else {
      setError("Incorrect passcode");
      toast("Invalid passcode", { type: "error" });
      setIsLoading(false);
      return;
    }
  }

  async function resendTemporaryPasscode(value: string) {
    (async () => {
      try {
        const response = await api.auth.learnisticTempPasscodeCreate(
          form.phone,
          { captchaResponse: value }
        );

        if (response.data.status === "200") {
          toast("Temporary passcode resent", {
            type: "success",
          });

          setTimer({ min: 2, sec: 59 });
          setDate(dayjs().add(180, "seconds"));
          setEnableResend(false);
        }
      } catch (error) {
        toast("Unexpected error", { type: "error" });
      } finally {
        setShowCaptcha(false);
      }
    })();
  }

  return (
    <PasscodeContainer>
      <PasscodeWrapper>
        <PasscodeInput
          passcode={form.temporaryPasscode}
          handleChangePasscode={(newPassCode) =>
            handleChangeForm("temporaryPasscode", newPassCode)
          }
          error={error !== ""}
          handleChangeError={(newError) => setError(newError)}
        />
      </PasscodeWrapper>

      <PasscodeButtonsWrapper>
        <StyledButton
          onClick={() => setSelectedForm(SignupFormSection.Create)}
          inverted
        >
          Back
        </StyledButton>
        <StyledButton
          disable={form.temporaryPasscode.includes("")}
          isLoading={isLoading}
          onClick={() => {
            if (isLoading) return;
            setIsLoading(true);
            handleClick();
          }}
        >
          Next
        </StyledButton>
      </PasscodeButtonsWrapper>

      <ClockTitle>
        {enableResend
          ? "Temporary passcode expired."
          : `Resend passcode in ${timer.min.toLocaleString("en-US", {
              minimumIntegerDigits: 2,
            })}:${timer.sec.toLocaleString("en-US", {
              minimumIntegerDigits: 2,
            })}`}
      </ClockTitle>

      {showCapctha ? (
        <ReCAPTCHA
          ref={recaptchaRef}
          sitekey={CAPTCHA_PUBLIC_KEY}
          onChange={resendTemporaryPasscode}
        />
      ) : (
        <ResendTitle
          onClick={() => {
            if (!enableResend) return;

            setShowCaptcha(true);
          }}
          css={
            !enableResend &&
            css`
              cursor: not-allowed;
              opacity: 0.7;
            `
          }
        >
          Resend passcode
        </ResendTitle>
      )}
    </PasscodeContainer>
  );
}
